/**
 * Wizard de creación de SRPs — 5 pasos (ADR_006).
 *
 *   1. Identidad: area + nombre + descripción + formato de reporte
 *   2. Criterios: lista editable de observations (code/label/description)
 *   3. Test drive: dry-run contra CDR de 1 día (preview, no persiste)
 *   4. Revisión: read-only summary
 *   5. Confirmación: POST /api/v1/ecc/srps → redirect a catálogo
 *
 * El SRP se crea en `status='proposed'`; un admin lo aprueba/rechaza desde
 * el catálogo del hub. Steps 2 y 3 tienen estado de errores propio para que
 * el usuario vea qué corregir antes de avanzar.
 */
import { AlertCircle, CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import type {
  CreateSrpRequest,
  DryRunSrpResponse,
  EccArea,
  SrpObservation,
} from "@/api/types";
import { useCreateSrp, useDryRunSrp } from "@/hooks/useEcc";
import { cn } from "@/lib/utils";

// ────────────────────────────────────────────────────────────────────────────
// Constantes + tipos

const AREAS: { value: EccArea; label: string; desc: string }[] = [
  { value: "nova", label: "NOVA", desc: "Extensiones 749/736/735/732" },
  { value: "soporte", label: "Soporte", desc: "Extensiones 808/809/810/811" },
  { value: "crea", label: "CREA", desc: "Centro de Reacción de Emergencias y Atención" },
  { value: "custodias", label: "Custodias", desc: "Pendiente DPECC-F" },
];

const STEPS = [
  { num: 1, label: "Identidad" },
  { num: 2, label: "Criterios" },
  { num: 3, label: "Test drive" },
  { num: 4, label: "Revisión" },
  { num: 5, label: "Confirmar" },
] as const;

const OBSERVATION_CODE_RE = /^[A-Z]\d+$/;

interface WizardState {
  area: EccArea | null;
  name: string;
  description: string;
  output_format: "docx" | "xlsx";
  observations: SrpObservation[];
  dry_run_date: string; // YYYY-MM-DD
}

function emptyObservation(): SrpObservation {
  return { code: "", label: "", description: "" };
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function buildCreatePayload(s: WizardState): CreateSrpRequest {
  return {
    area: s.area!,
    name: s.name.trim(),
    description: s.description.trim(),
    output_format: s.output_format,
    config: {
      source: "portal_nuevo_srp",
      default_observations: s.observations.map((o) => ({
        code: o.code.trim().toUpperCase(),
        label: o.label.trim(),
        description: o.description?.trim() ?? "",
      })),
      workaround: false,
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Validaciones por paso

function validateStep1(s: WizardState): string[] {
  const errs: string[] = [];
  if (!s.area) errs.push("Selecciona un área.");
  const nameLen = s.name.trim().length;
  if (nameLen < 3) errs.push("El nombre debe tener al menos 3 caracteres.");
  if (nameLen > 128) errs.push("El nombre no puede pasar de 128 caracteres.");
  if (s.description.length > 2000)
    errs.push("La descripción no puede pasar de 2000 caracteres.");
  return errs;
}

function validateStep2(s: WizardState): string[] {
  const errs: string[] = [];
  if (s.observations.length === 0) {
    errs.push("Agrega al menos un criterio.");
    return errs;
  }
  const codes = new Set<string>();
  s.observations.forEach((o, i) => {
    const code = o.code.trim().toUpperCase();
    if (!OBSERVATION_CODE_RE.test(code)) {
      errs.push(`Criterio #${i + 1}: el código debe ser una letra seguida de números (ej. C1, S12).`);
    } else if (codes.has(code)) {
      errs.push(`Criterio #${i + 1}: el código ${code} está repetido.`);
    } else {
      codes.add(code);
    }
    if (o.label.trim().length === 0)
      errs.push(`Criterio #${i + 1}: la etiqueta no puede estar vacía.`);
    if (o.label.length > 200)
      errs.push(`Criterio #${i + 1}: la etiqueta no puede pasar de 200 caracteres.`);
    if ((o.description ?? "").length > 500)
      errs.push(`Criterio #${i + 1}: la descripción no puede pasar de 500 caracteres.`);
  });
  return errs;
}

// ────────────────────────────────────────────────────────────────────────────
// Componente principal

export function NewSRPPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [state, setState] = useState<WizardState>({
    area: null,
    name: "",
    description: "",
    output_format: "docx",
    observations: [emptyObservation()],
    dry_run_date: isoToday(),
  });
  const [stepErrors, setStepErrors] = useState<string[]>([]);

  const createMut = useCreateSrp();
  const dryRunMut = useDryRunSrp();

  const goNext = () => {
    let errs: string[] = [];
    if (step === 1) errs = validateStep1(state);
    if (step === 2) errs = validateStep2(state);
    setStepErrors(errs);
    if (errs.length === 0 && step < 5) {
      setStep((step + 1) as typeof step);
    }
  };

  const goBack = () => {
    setStepErrors([]);
    if (step > 1) setStep((step - 1) as typeof step);
  };

  const handleCreate = async () => {
    setStepErrors([]);
    try {
      const payload = buildCreatePayload(state);
      const res = await createMut.mutateAsync(payload);
      // Redirige al catálogo del area (el área va como query param, no path).
      // El SRP nuevo aparecerá en la lista con badge "Propuesto" para que un
      // admin lo apruebe.
      navigate(`/ecc/catalog?area=${state.area}&srp_created=${res.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setStepErrors([err.message]);
      } else {
        setStepErrors(["Error inesperado al crear el SRP."]);
      }
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Define un nuevo SRP de calidad. Se creará en estado <em>propuesto</em>; un
        administrador lo aprobará desde el catálogo para que sea ejecutable en
        producción.
      </p>

      <StepNav current={step} />

      {stepErrors.length > 0 && <ErrorBox errors={stepErrors} />}

      <div className="rounded-lg border bg-card p-6">
        {step === 1 && (
          <Step1Identity
            state={state}
            onChange={(patch) => setState((s) => ({ ...s, ...patch }))}
          />
        )}
        {step === 2 && (
          <Step2Criteria
            observations={state.observations}
            onChange={(observations) =>
              setState((s) => ({ ...s, observations }))
            }
          />
        )}
        {step === 3 && (
          <Step3DryRun
            state={state}
            onChangeDate={(d) => setState((s) => ({ ...s, dry_run_date: d }))}
            dryRunMut={dryRunMut}
          />
        )}
        {step === 4 && <Step4Review state={state} />}
        {step === 5 && (
          <Step5Confirm
            state={state}
            onCreate={handleCreate}
            creating={createMut.isPending}
          />
        )}
      </div>

      <footer className="flex items-center justify-between">
        <button
          type="button"
          onClick={goBack}
          disabled={step === 1}
          className="rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          Atrás
        </button>
        {step < 5 ? (
          <button
            type="button"
            onClick={goNext}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Siguiente
          </button>
        ) : (
          <button
            type="button"
            onClick={handleCreate}
            disabled={createMut.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createMut.isPending && <Loader2 className="size-4 animate-spin" />}
            Crear SRP
          </button>
        )}
      </footer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-componentes

function StepNav({ current }: { current: number }) {
  return (
    <ol className="flex flex-wrap gap-2">
      {STEPS.map((s) => {
        const state =
          current === s.num
            ? "current"
            : current > s.num
              ? "done"
              : "pending";
        return (
          <li
            key={s.num}
            className={cn(
              "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium",
              state === "current" && "border-primary bg-primary text-primary-foreground",
              state === "done" && "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
              state === "pending" && "border-border bg-card text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "flex size-5 items-center justify-center rounded-full text-[10px] font-semibold",
                state === "current" && "bg-primary-foreground text-primary",
                state === "done" && "bg-emerald-600 text-white",
                state === "pending" && "bg-muted text-muted-foreground",
              )}
            >
              {state === "done" ? "✓" : s.num}
            </span>
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

function ErrorBox({ errors }: { errors: string[] }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    >
      <div className="mb-1 flex items-center gap-2 font-medium">
        <AlertCircle className="size-4" />
        Corrige antes de continuar:
      </div>
      <ul className="list-inside list-disc space-y-0.5 text-xs">
        {errors.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

// Paso 1 — Identidad
function Step1Identity({
  state,
  onChange,
}: {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 text-sm font-medium">Área</div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {AREAS.map((a) => {
            const active = state.area === a.value;
            return (
              <button
                key={a.value}
                type="button"
                onClick={() => onChange({ area: a.value })}
                className={cn(
                  "rounded-md border px-4 py-3 text-left transition-colors",
                  active
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-accent",
                )}
              >
                <div className="text-sm font-medium">{a.label}</div>
                <div className="text-xs text-muted-foreground">{a.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label htmlFor="srp-name" className="mb-1 block text-sm font-medium">
          Nombre del SRP
        </label>
        <input
          id="srp-name"
          type="text"
          value={state.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Ej: SRP NOVA — atención cliente Q2 2026"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          maxLength={128}
        />
        <div className="mt-1 text-xs text-muted-foreground">
          {state.name.length}/128
        </div>
      </div>

      <div>
        <label htmlFor="srp-desc" className="mb-1 block text-sm font-medium">
          Descripción
        </label>
        <textarea
          id="srp-desc"
          value={state.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Explica brevemente qué evalúa este SRP y a quién aplica."
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          rows={3}
          maxLength={2000}
        />
        <div className="mt-1 text-xs text-muted-foreground">
          {state.description.length}/2000
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-medium">Formato del reporte</div>
        <div className="flex gap-2">
          {(["docx", "xlsx"] as const).map((fmt) => {
            const active = state.output_format === fmt;
            return (
              <button
                key={fmt}
                type="button"
                onClick={() => onChange({ output_format: fmt })}
                className={cn(
                  "flex-1 rounded-md border px-4 py-2 text-sm font-medium transition-colors",
                  active
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-accent",
                )}
              >
                {fmt === "docx" ? "Word (.docx)" : "Excel (.xlsx)"}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Paso 2 — Criterios
function Step2Criteria({
  observations,
  onChange,
}: {
  observations: SrpObservation[];
  onChange: (next: SrpObservation[]) => void;
}) {
  const updateAt = (idx: number, patch: Partial<SrpObservation>) => {
    onChange(
      observations.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    );
  };
  const removeAt = (idx: number) => {
    onChange(observations.filter((_, i) => i !== idx));
  };
  const addOne = () => {
    onChange([...observations, emptyObservation()]);
  };

  return (
    <div className="space-y-4">
      <div className="text-sm">
        <div className="font-medium">Criterios de evaluación</div>
        <div className="text-muted-foreground">
          Cada criterio se evalúa por separado en cada llamada. El código
          identifica el criterio en el reporte (ej. C1, C2). Necesitas al
          menos uno.
        </div>
      </div>

      <div className="space-y-3">
        {observations.map((obs, i) => (
          <div
            key={i}
            className="grid grid-cols-12 gap-3 rounded-md border bg-background p-3"
          >
            <div className="col-span-2">
              <label className="mb-1 block text-[11px] font-medium uppercase text-muted-foreground">
                Código
              </label>
              <input
                type="text"
                value={obs.code}
                onChange={(e) =>
                  updateAt(i, { code: e.target.value.toUpperCase() })
                }
                placeholder="C1"
                maxLength={8}
                className="w-full rounded-md border bg-background px-2 py-1.5 font-mono text-sm uppercase focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="col-span-9">
              <label className="mb-1 block text-[11px] font-medium uppercase text-muted-foreground">
                Etiqueta
              </label>
              <input
                type="text"
                value={obs.label}
                onChange={(e) => updateAt(i, { label: e.target.value })}
                placeholder="Saluda al cliente"
                maxLength={200}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="col-span-1 flex items-end justify-end">
              <button
                type="button"
                onClick={() => removeAt(i)}
                aria-label={`Eliminar criterio ${i + 1}`}
                className="rounded-md border border-destructive/30 p-1.5 text-destructive transition-colors hover:bg-destructive/10"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
            <div className="col-span-12">
              <label className="mb-1 block text-[11px] font-medium uppercase text-muted-foreground">
                Descripción (opcional)
              </label>
              <input
                type="text"
                value={obs.description ?? ""}
                onChange={(e) =>
                  updateAt(i, { description: e.target.value })
                }
                placeholder="Pistas para el modelo: frases tipo 'buenos días', 'cómo le va'"
                maxLength={500}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addOne}
        className="inline-flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
      >
        <Plus className="size-4" />
        Agregar criterio
      </button>
    </div>
  );
}

// Paso 3 — Test drive (dry-run)
function Step3DryRun({
  state,
  onChangeDate,
  dryRunMut,
}: {
  state: WizardState;
  onChangeDate: (d: string) => void;
  dryRunMut: ReturnType<typeof useDryRunSrp>;
}) {
  const [result, setResult] = useState<DryRunSrpResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRun = async () => {
    setError(null);
    setResult(null);
    try {
      const candidate = buildCreatePayload(state);
      const res = await dryRunMut.mutateAsync({
        period_date: state.dry_run_date,
        candidate,
      });
      setResult(res);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Error inesperado al ejecutar el test drive.");
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm">
        <div className="font-medium">Test drive del SRP</div>
        <div className="text-muted-foreground">
          Corre el SRP contra las llamadas de un día específico — sin generar
          reporte ni persistir nada. Es un sanity check antes de proponerlo
          formalmente. Este paso es opcional pero recomendado.
        </div>
      </div>

      <div className="flex items-end gap-3">
        <div>
          <label htmlFor="dry-run-date" className="mb-1 block text-sm font-medium">
            Fecha de prueba
          </label>
          <input
            id="dry-run-date"
            type="date"
            value={state.dry_run_date}
            onChange={(e) => onChangeDate(e.target.value)}
            className="rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <button
          type="button"
          onClick={handleRun}
          disabled={dryRunMut.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {dryRunMut.isPending && <Loader2 className="size-4 animate-spin" />}
          Probar
        </button>
      </div>

      {error && <ErrorBox errors={[error]} />}

      {result && (
        <div className="space-y-3 rounded-md border bg-muted/30 p-4">
          {result.warnings.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
              {result.warnings.map((w, i) => (
                <div key={i}>⚠ {w}</div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase text-muted-foreground">
                Llamadas totales
              </div>
              <div className="text-2xl font-semibold">{result.calls_total}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">
                Con grabación
              </div>
              <div className="text-2xl font-semibold">
                {result.calls_with_recording}
              </div>
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs uppercase text-muted-foreground">
              Cumplimiento por criterio
            </div>
            <div className="space-y-1">
              {result.by_criterion.map((c) => (
                <div
                  key={c.code}
                  className="flex items-center justify-between rounded-md bg-card px-3 py-2 text-sm"
                >
                  <div>
                    <span className="font-mono text-xs font-semibold text-primary">
                      {c.code}
                    </span>
                    <span className="ml-2">{c.label}</span>
                  </div>
                  <div className="font-mono text-xs">
                    {c.matched} ({c.pct.toFixed(1)}%)
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Paso 4 — Revisión
function Step4Review({ state }: { state: WizardState }) {
  const areaLabel = useMemo(
    () => AREAS.find((a) => a.value === state.area)?.label ?? "—",
    [state.area],
  );
  return (
    <div className="space-y-4">
      <div className="text-sm">
        <div className="font-medium">Revisa la información antes de crear</div>
        <div className="text-muted-foreground">
          Una vez creado, el SRP queda en estado <em>propuesto</em> hasta que
          un administrador lo apruebe.
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-3 rounded-md border bg-muted/30 p-4 sm:grid-cols-2">
        <SummaryRow label="Área" value={areaLabel} />
        <SummaryRow label="Formato" value={state.output_format.toUpperCase()} />
        <SummaryRow label="Nombre" value={state.name} />
        <SummaryRow label="Criterios" value={`${state.observations.length}`} />
      </dl>

      {state.description && (
        <div>
          <div className="mb-1 text-xs uppercase text-muted-foreground">
            Descripción
          </div>
          <div className="rounded-md border bg-background p-3 text-sm">
            {state.description}
          </div>
        </div>
      )}

      <div>
        <div className="mb-2 text-xs uppercase text-muted-foreground">
          Criterios
        </div>
        <div className="space-y-1">
          {state.observations.map((o, i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-md border bg-background p-3 text-sm"
            >
              <span className="font-mono text-xs font-semibold text-primary">
                {o.code.toUpperCase()}
              </span>
              <div className="flex-1">
                <div className="font-medium">{o.label}</div>
                {o.description && (
                  <div className="text-xs text-muted-foreground">
                    {o.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
}

// Paso 5 — Confirmación
function Step5Confirm({
  state,
  onCreate,
  creating,
}: {
  state: WizardState;
  onCreate: () => void;
  creating: boolean;
}) {
  return (
    <div className="space-y-4 text-center">
      <CheckCircle2 className="mx-auto size-12 text-emerald-500" />
      <h2 className="text-lg font-semibold">Listo para proponer el SRP</h2>
      <p className="mx-auto max-w-md text-sm text-muted-foreground">
        El SRP <span className="font-medium text-foreground">{state.name}</span>{" "}
        se creará en estado <em>propuesto</em>. Aparecerá en el catálogo del
        hub para que un administrador lo apruebe.
      </p>
      <button
        type="button"
        onClick={onCreate}
        disabled={creating}
        className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {creating && <Loader2 className="size-4 animate-spin" />}
        Crear SRP propuesto
      </button>
    </div>
  );
}
